import { useHead } from "@unhead/vue";
import { computedEager } from "@vueuse/core";
import { unref } from "vue";
import { useI18n } from "vue-i18n";
import { useEnvironmentName } from "@/core/composables/useEnvironmentName";
import { cleanHtmlForTitle } from "@/core/utilities";
import { useThemeContext } from "./useThemeContext";
import type { IUsePageSeoData } from "../types";

const JA_STORE_NAME = "ソーラボジャパン (Thorlabs)";

export function usePageHead(data: IUsePageSeoData) {
  const { themeContext } = useThemeContext();
  const { environmentName, isIgnored } = useEnvironmentName();
  const { locale } = useI18n();

  const {
    storeName,
    settings: { page_title_with_store_name, page_title_store_name_align, page_title_divider },
  } = themeContext.value;

  return useHead({
    title: computedEager(() => {
      if (!data.title) {
        return "";
      }

      const textOrChunks = unref(data.title);
      const titleChunks: string[] = [];

      if (Array.isArray(textOrChunks)) {
        titleChunks.push(...textOrChunks.map(cleanHtmlForTitle));
      } else if (textOrChunks) {
        titleChunks.push(cleanHtmlForTitle(textOrChunks));
      }

      if (page_title_with_store_name) {
        const isJa = locale.value === "ja";
        const effectiveStoreName = isJa ? JA_STORE_NAME : storeName;
        const align = isJa ? "end" : page_title_store_name_align;
        titleChunks[align === "end" ? "push" : "unshift"](effectiveStoreName);
      }

      if (!isIgnored) {
        titleChunks.unshift(environmentName);
      }

      return titleChunks.filter(Boolean).join(page_title_divider);
    }),
    meta: () => {
      if (!data.meta) {
        return [];
      }
      return Object.entries(data.meta).map(([name, content]) => ({
        [name.startsWith("og:") ? "property" : "name"]: name,
        content: unref(content) ?? "",
      }));
    },
  });
}
